import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';
import { CacheService } from './cache.service';
import { CommonService } from './common.service';
import { ErrorMessage } from './model/common.interfaces';
import { Dictionary } from './model/dictionary';
import { IToastService, TOAST_SERVICE } from './toast.interface';

export declare type Caller = string;
export interface HttpRequestTracker {
  tracker: Subject<void>;
  cacheKey?: string;
}

@Injectable()
export class HttpService {
  // Resolve HTTP using the constructor
  constructor(
    private httpClient: HttpClient,
    private commonService: CommonService,
    private cacheService: CacheService,
    @Inject(TOAST_SERVICE) private toastService: IToastService
  ) {}
  private requestTrackerList: Dictionary<HttpRequestTracker> = {};

  public clearRequestTrackerList(caller: Caller) {}

  public get<T>(url: string, requestParams?: HttpParams, caller?: Caller): Observable<T> {
    const getRequest = this.requestWrapper(
      this.httpClient.get<T>(this.commonService.constructUrl(url)),
      caller
    ).pipe(
      map(data => {
        if (typeof data == 'string') {
          return JSON.parse(data);
        }
        return data;
      })
    );

    return getRequest;
  }

  download<T>(url: string, data: any, caller?: Caller): Observable<T> {
    const HTTPOptions = {
      headers: new HttpHeaders({
        Accept: 'application/pdf'
      }),
      responseType: 'blob' as 'json'
    };
    const newRequestCancelRef: Subject<void> = this.cancelPreviousRequestIfAny(caller);
    const callback = data
      ? this.httpClient.post<T>(this.commonService.constructUrl(url), data, HTTPOptions)
      : this.httpClient.get<T>(this.commonService.constructUrl(url), HTTPOptions);
    const postRequest = callback.pipe(
      catchError(this.fileHandleError<T>()),
      takeUntil(newRequestCancelRef)
    );
    return postRequest;
  }
  private fileHandleError<T>() {
    return (err: HttpErrorResponse): Observable<never> => {
      this.handleError<T>()(
        err && err?.error && err?.error?.size === 0 ? 'File not found' : err?.statusText,
        throwError(() => err)
      );
      return throwError(() => err);
    };
  }

  post<T>(
    url: string,
    data: any,
    caller?: Caller,
    cacheKey?: string,
    supressErrorCodes?: number[],
    httpOptions?: Dictionary<T>
  ): Observable<T> {
    const postRequest = this.requestWrapper(
      this.httpClient.post<T>(this.commonService.constructUrl(url), data, httpOptions),
      caller,
      cacheKey,
      supressErrorCodes
    );

    return postRequest;
  }

  private requestWrapper<T>(
    source: Observable<T>,
    caller: Caller,
    cacheKey?: string,
    supressErrorCodes?: number[]
  ) {
    const newRequestCanelRef = this.cancelPreviousRequestIfAny(caller, cacheKey);
    return source.pipe(
      catchError(this.handleError<T>(supressErrorCodes)),
      tap(data => {
        this.closeExistingTrackerSubject(caller, false);
      }),
      takeUntil(this.getTracker(newRequestCanelRef))
    );
  }

  private getTracker(newRequestCanelRef: Subject<void>): Observable<any> {
    return newRequestCanelRef || new Subject<void>();
  }

  private handleError<T>(
    supressErrorCodes?: number[]
  ): (err: any, caught: Observable<T>) => Observable<never> {
    return (err: HttpErrorResponse) => {
      const error = err.error ? err.error : err;

      if (err && err.status === 403) {
        //if error status is Forbidden (403), need to know this status in calling functions.
        console.error(error);
        return throwError(() => err);
      }

      const canShowThisError = !supressErrorCodes?.includes((error as ErrorMessage).ErrorCode);
      //Not to show a common error box, so that this error message can be displayed in the calling Context.
      if (!error.HasContext && canShowThisError) {
        this.showError(error);
      }
      return throwError(() => error);
    };
  }

  put<T>(url: string, body: any): Observable<T> {
    return this.httpClient
      .put(this.commonService.constructUrl(url), body) // ...using put request
      .pipe(
        catchError(this.handleError<T>()),
        map((data: T) => data)
      );
  }

  delete<T>(url: string, caller?: Caller): Observable<T> {
    const deleteRequest = this.requestWrapper(
      this.httpClient.delete(this.commonService.constructUrl(url)).pipe(map((data: T) => data)),
      caller || url,
      null
    );

    return deleteRequest;
  }

  private cancelPreviousRequestIfAny(caller: Caller, cacheKey?: string) {
    const requestCancelRef: Subject<void> = new Subject<void>();
    if (caller) {
      this.closeExistingTrackerSubject(caller);
      this.requestTrackerList[caller] = { tracker: requestCancelRef, cacheKey };
    }
    return requestCancelRef;
  }

  private closeExistingTrackerSubject(caller: Caller, close = true) {
    if (!caller) return false;

    const existingCall = this.requestTrackerList[caller];
    if (existingCall && existingCall.tracker) {
      if (close) {
        // This aborts all HTTP requests.
        existingCall.tracker.next();
        // This completes the subject properlly. So that this will trigger the takeUntil mentioned in all http calls.
        existingCall.tracker.complete();
        //Deletes cancelRef from request tracker.
        if (existingCall.cacheKey) {
          this.cacheService.clearInflightRequest(existingCall.cacheKey);
        }
      }
      delete existingCall.tracker;
      delete this.requestTrackerList[caller];
      return true;
    }
    return false;
  }

  private showError(error: ErrorMessage | ErrorMessage[]) {
    if (!error) return;

    let msg: string;
    if (Array.isArray(error)) {
      msg = error
        .map(errorMessage => errorMessage.ExceptionMessage || errorMessage.Message)
        .join('<br/>');
    } else {
      msg = error.ExceptionMessage || error.Message || error?.error_description || error.toString();
    }

    if (msg) {
      this.toastService.openErrorToast(msg);
    }
  }
}
